import { ValidityState } from "element-internals-polyfill/dist/ValidityState";

declare global {
    interface Window {
        ValidityMessages: {
            [key: string]: { [key: string]: string };
        };
    }
}

export interface ICustomFormElement {
    template: HTMLTemplateElement;
    validity: ValidityState;
    reportsValidity: boolean;
}

export abstract class AbstractFormElement extends HTMLElement {
    declare shadowStyle: HTMLTemplateElement;
    declare template: HTMLTemplateElement;
    protected mainEvent: string = "input";

    protected internals: ElementInternals;
    protected label: HTMLLabelElement;
    protected inputSelector: string = ":scope input";
    protected shadow: ShadowRoot;
    protected inputWrapper: HTMLSpanElement;
    protected input: HTMLInputElement | HTMLTextAreaElement;
    protected clickHandler: (e?: Event) => void;
    protected submitListener: (e?: Event) => void;
    protected form: HTMLFormElement;

    static formAssociated = true;

    static observedAttributes = ["required", "readonly", "disabled", "placeholder"];

    constructor() {
        super();
        this.clickHandler = () => this.input.focus();
        this.submitListener = () => (this.reportsValidity = true);
    }

    /**
     * initialize when added to dom
     */
    connectedCallback() {
        this.internals = this.attachInternals();
        this.form = this.internals.form;
        this.appendChild(this.template.content.cloneNode(true));
        this.initShadow().initElements().initAttributes().attachEvents().initInternals();
        this.validate();
    }

    disconnectedCallback() {
        this.detachEvents();
        delete this.form;
    }

    /**
     * handle attribute changes
     * @param {String} name
     * @param {String} o
     * @param {String} n
     */
    attributeChangedCallback(name: string, o: string, n: string) {
        if (this.input) {
            switch (name) {
                case "required":
                    this.required = !!n;
                    break;
                case "readonly":
                    this.readonly = !!n;
                    break;
                case "disabled":
                    this.disabled = !!n;
                    break;
                case "placeholder":
                    this.input.placeholder = n;
                    if (this.label.querySelector<HTMLSpanElement>("span.label")) {
                        this.label.querySelector<HTMLSpanElement>("span.label").innerHTML = this.input.placeholder;
                    }
                    break;
            }
        }
    }

    initShadow() {
        this.inputWrapper = this.querySelector<HTMLSpanElement>("span.input");
        if (this.inputWrapper) {
            this.input = document.createElement("input");
            this.input.name = this.getAttribute("name");
            if (this.hasAttribute("autocomplete")) {
                this.input.autocomplete = this.getAttribute("autocomplete");
            }
            if (this.hasAttribute("autocapitalize")) {
                this.input.autocapitalize = this.getAttribute("autocapitalize");
            }
            if ("undefined" === typeof this.dataset.skipShadow) {
                this.shadow = this.inputWrapper.attachShadow({ mode: "open" });
                this.shadow.appendChild(this.shadowStyle.content.cloneNode(true));
                this.shadow.appendChild(this.input);
            } else {
                this.inputWrapper.appendChild(this.input);
            }
        }
        return this;
    }

    /**
     * initialize elements
     */
    initElements() {
        this.input.placeholder = this.getAttribute("placeholder") ?? "";
        this.label = this.querySelector<HTMLLabelElement>("label");
        if (this.label.querySelector<HTMLSpanElement>("span.label")) {
            this.label.querySelector<HTMLSpanElement>("span.label").innerHTML = this.input.placeholder;
        }
        if (this.hasAttribute("value")) {
            this.value = this.getAttribute("value");
        }
        if (this.dataset.help) {
            this.querySelector<HTMLElement>("span.help").innerText = this.dataset.help;
        }
        return this;
    }

    /**
     * initialize attributes
     */
    initAttributes() {
        this.required = this.hasAttribute("required");
        this.readonly = this.hasAttribute("readonly");
        this.disabled = this.hasAttribute("disabled");
        return this;
    }

    /**
     * add event listeners
     */
    attachEvents() {
        this.input.addEventListener("focus", () => this.label.classList.add("validates"));
        this.internals.form?.addEventListener("submit", this.submitListener);
        this.input.addEventListener("blur", () => this.validate());
        this.input.addEventListener(this.mainEvent, (e) => {
            this.validate();
            this.dispatchEvent(new Event(this.mainEvent, e));
            this.internals.form?.dispatchEvent(new CustomEvent("formchange", { detail: this }));
            this.label.classList.toggle("has-value", this.value !== "");
            // @ts-ignore
            this.internals.states[this.value !== "" ? "add" : "delete"]("--has-value");
        });
        this.label.addEventListener("click", this.clickHandler);
        return this;
    }

    /**
     * detach listeners
     */
    detachEvents() {
        this.form?.removeEventListener("submit", this.submitListener);
    }

    /**
     * initialize internals
     */
    initInternals() {
        this.internals.setFormValue(this.value);
        return this;
    }

    /**
     * validation
     */
    validate() {
        this.internals.setFormValue(this.value);
        if (this.input.willValidate) {
            if (this.input.validationMessage !== "custom-error") {
                // we should not undo our descendants custom validation
                // reset validityState.customError first
                this.input.setCustomValidity("");
            }
            if (!this.input.validity.valid) {
                const message = this.matchValidityMessage();
                if (message) {
                    this.input.setCustomValidity(message);
                }
            } else {
                this.input.setCustomValidity("");
            }
            this.internals.setValidity(this.input.validity, this.input.validationMessage, this.input);
        }
        this.querySelector<HTMLElement>("span.error").innerText = this.input.validationMessage;
        this.classList.toggle("valid", this.input.validity.valid);
        return this.input.validity.valid;
    }

    resetValidationState() {
        this.input?.setCustomValidity("");
        this.label?.classList.remove("validates");
    }

    /**
     * obtain validation message
     */
    matchValidityMessage() {
        let message, customMessages;
        // obtain message from attribute
        try {
            customMessages = JSON.parse(this.dataset.messages);
            message = Object.keys(customMessages).find((k) => {
                // @ts-ignore
                return this.input.validity[k] === true;
            });
            if (message) {
                return customMessages[message];
            }
        } catch (error) {
            /* empty */
        }

        // obtain message by tagname
        if (window.ValidityMessages[this.tagName]) {
            message = Object.keys(window.ValidityMessages[this.tagName]).find((k) => {
                // @ts-ignore
                return this.input.validity[k] === true;
            });
            if (message) {
                return window.ValidityMessages[this.tagName][message];
            }
        }

        // obtain message by input type
        if (window.ValidityMessages[this.input.type]) {
            message = Object.keys(window.ValidityMessages[this.input.type]).find((k) => {
                // @ts-ignore
                return this.input.validity[k] === true;
            });
            if (message) {
                return window.ValidityMessages[this.input.type][message];
            }
        }

        // obtain default message
        if (window.ValidityMessages.default) {
            message = Object.keys(window.ValidityMessages.default).find((k: string) => {
                // @ts-ignore
                return this.input.validity[k] === true;
            });

            return window.ValidityMessages.default[message];
        }
        return undefined;
    }

    select() {
        this.input.select();
    }

    get required() {
        return this.input.required;
    }

    set required(v: boolean) {
        if (!v) {
            this.removeAttribute("required");
        } else if (!this.hasAttribute("required")) {
            this.setAttribute("required", v.toString());
        }
        this.input.required = v;
        this.validate();
    }

    get value() {
        return this.input.value;
    }

    set value(v: string) {
        this.input.value = v;
        this.label.classList.toggle("has-value", !!this.input.value);
        this.validate();
    }

    set name(v) {
        this.input.name = v;
        this.setAttribute("name", v);
    }

    get name() {
        return this.input.name;
    }

    get readonly() {
        return this.input.hasAttribute("readonly");
    }

    set readonly(v: boolean) {
        if (v) {
            this.input.setAttribute("readonly", "readonly");
            this.label.setAttribute("readonly", "readonly");
        } else {
            this.input.removeAttribute("readonly");
        }
        this.validate();
    }

    get disabled() {
        return this.input.disabled;
    }

    set disabled(v: boolean) {
        const hasDisabledAttribute = this.hasAttribute("disabled");
        if (v && !hasDisabledAttribute) {
            this.setAttribute("disabled", "disabled");
        } else if (!v && hasDisabledAttribute) {
            this.removeAttribute("disabled");
        }
        this.input.disabled = v;
        this.label.classList.toggle("disabled", this.disabled);
        this.validate();
    }

    get validity() {
        return this.input.validity;
    }

    get reportsValidity() {
        return this.label?.classList.contains("validates");
    }

    set reportsValidity(v: boolean) {
        this.label?.classList.toggle("validates", !!v);
    }

    get willValidate() {
        return this.input.willValidate;
    }

    set help(v: string) {
        const node = this.querySelector<HTMLElement>("span.help");
        if (node) {
            node.innerText = v;
        }
    }
}

AbstractFormElement.prototype.shadowStyle = document.createElement("template");
AbstractFormElement.prototype.shadowStyle.innerHTML = /*html*/ `
<style>
input {
    border: var(--shadow-input-border, none);
    outline: var(--shadow-input-outline, none);
    width: var(--shadow-input-width, auto);
    display: var(--shadow-input-display, inline-block);
    background-color: var(--shadow-input-bg-color, transparent);
    min-width: var(--shadow-input-min-width, 2rem);
    cursor: var(--shadow-input-cursor, auto);
    accent-color: var(--shadow-input-accent-color, auto);
    font-size:  var(--shadow-input-font-size, 1rem);
    font-weight:  var(--shadow-input-font-weight, normal);
    font-family:  var(--shadow-input-font-family, sans-serif);
    line-height: var(--shadow-input-line-height, normal);
    color: var(--shadow-input-fg-color, black);
    padding: var(--shadow-input-padding, 0);
    text-align: var(--shadow-input-text-align, left);
}
input::placeholder {
    color: var(--shadow-input-placeholder-color, transparent);;
}
</style>
`;
